import { useState } from 'react';
import { Link } from 'react-router-dom';
import SiteNavbar from '@/components/feature/SiteNavbar';
import SiteFooter from '@/components/feature/SiteFooter';
import { nomoStories, storyCategories, type StoryCategory } from '@/mocks/stories';
import { useScrollReveal } from '@/hooks/useScrollReveal';

type Filter = 'ALL' | StoryCategory;

const chipTone: Record<StoryCategory, string> = {
  FOOD: 'bg-primary-100 text-primary-700',
  PLACE: 'bg-secondary-100 text-secondary-700',
  PEOPLE: 'bg-accent-100 text-accent-700',
  CHARACTER: 'bg-primary-100 text-primary-700',
  PLAY: 'bg-secondary-100 text-secondary-700',
};

export default function StoriesPage() {
  useScrollReveal();
  const [filter, setFilter] = useState<Filter>('ALL');

  const list = filter === 'ALL' ? nomoStories : nomoStories.filter((s) => s.category === filter);
  const activeCategory = storyCategories.find((c) => c.key === filter);

  return (
    <div className="min-h-screen w-full bg-background-50">
      <SiteNavbar />
      <main>
        <section className="bg-background-100 pb-14 pt-28 md:pb-20 md:pt-40">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <nav className="reveal mb-8 flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
              <Link
                to="/"
                className="transition-colors duration-300 hover:text-primary-600 cursor-pointer"
              >
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-300"></i>
              <span className="text-foreground-800">品牌故事</span>
            </nav>

            <div className="reveal">
              <div className="mb-6 flex items-center gap-3">
                <span className="text-[11px] tracking-[0.34em] text-primary-600 uppercase">
                  NOMOISM Stories
                </span>
                <span className="h-px w-8 bg-primary-300"></span>
                <span className="text-[11px] tracking-[0.28em] text-foreground-400 uppercase">
                  内容型品牌资产库
                </span>
              </div>
              <h1 className="font-heading text-4xl leading-tight tracking-[0.06em] text-foreground-950 md:text-6xl">
                每一个角色，都有自己的故事
              </h1>
              <p className="mt-6 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                这里不只是品牌新闻，而是一座内容型品牌资产库：从一道菜，到一个地方，到一群人，最后到一个角色。
                点开任意一篇，读一段完整的来龙去脉。
              </p>
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-14 md:py-20">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <div className="reveal mb-8 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setFilter('ALL')}
                className={[
                  'rounded-full px-4 py-2 text-xs tracking-[0.08em] transition-colors duration-300 cursor-pointer whitespace-nowrap',
                  filter === 'ALL'
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:text-primary-600',
                ].join(' ')}
              >
                全部故事
              </button>
              {storyCategories.map((category) => (
                <button
                  key={category.key}
                  type="button"
                  onClick={() => setFilter(category.key)}
                  className={[
                    'rounded-full px-4 py-2 text-xs tracking-[0.08em] transition-colors duration-300 cursor-pointer whitespace-nowrap',
                    filter === category.key
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:text-primary-600',
                  ].join(' ')}
                >
                  {category.zh}
                </button>
              ))}
            </div>

            {activeCategory && (
              <p className="reveal mb-6 text-sm text-foreground-500">
                {activeCategory.zh} — {activeCategory.label}
              </p>
            )}

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {list.map((story, index) => (
                <Link
                  key={story.id}
                  to={`/stories/${story.id}`}
                  className="reveal group block overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-colors duration-300 hover:border-primary-200"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="relative h-[220px] w-full overflow-hidden bg-background-100">
                    <img
                      src={story.image}
                      alt={`NOMOISM 品牌故事：${story.title}`}
                      title={`NOMOISM ${story.categoryZh} · ${story.title}`}
                      className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.05]"
                    />
                    <span
                      className={`absolute left-4 top-4 rounded-full px-3 py-1 text-[10px] tracking-[0.14em] ${chipTone[story.category]}`}
                    >
                      {story.category}
                    </span>
                  </div>

                  <div className="p-6">
                    <p className="text-[11px] tracking-[0.2em] text-foreground-400 uppercase">
                      {story.categoryZh} · {story.date}
                    </p>
                    <h2 className="mt-3 font-heading text-lg leading-snug text-foreground-950">
                      {story.title}
                    </h2>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-600">{story.excerpt}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm text-primary-600">
                      阅读全文
                      <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}